import React, { useState } from "react";
import { Alert, Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, Typography, App } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import {
  useGetTrashQuery,
  useRestoreTrashEntityMutation,
} from "../../../store/api/trashApi";
import { TrashEntity, TrashItem } from "../../../services/trash/trash.types";
import { useClampPageToTotal } from "../../../hooks/useClampPageToTotal";

dayjs.extend(relativeTime);

const { Text } = Typography;

const DEFAULT_LIMIT = 25;

interface EntityLabel {
  article: string;
  singular: string;
}

const ENTITY_LABELS: Record<TrashEntity, EntityLabel> = {
  resource: { article: "el", singular: "recurso" },
  lesson: { article: "la", singular: "lección" },
  course: { article: "el", singular: "curso" },
  program: { article: "el", singular: "programa" },
  project: { article: "el", singular: "proyecto" },
};

const formatReference = (item: { type: string; id: number; title: string }) =>
  `${item.type} #${item.id} ${item.title}`;

export interface TrashTableProps {
  entity: TrashEntity;
  days: number;
}

const TrashTable: React.FC<TrashTableProps> = ({ entity, days }) => {
  const { notification } = App.useApp();
  // The page lives in the URL (tab/days are owned by the parent); RTK Query
  // keys the cache by the whole arg, which replaces the old requestIdRef
  // anti-race guard: a stale response can't overwrite a newer combination.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;

  const { data, isFetching, isError, refetch } = useGetTrashQuery({
    entity,
    page,
    limit: DEFAULT_LIMIT,
    days,
  });
  const [restoreTrashEntity] = useRestoreTrashEntityMutation();
  const [restoringIds, setRestoringIds] = useState<Record<number, boolean>>({});

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? DEFAULT_LIMIT;

  const label = ENTITY_LABELS[entity];

  const setPage = (newPage: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newPage > 1) next.set("page", String(newPage));
        else next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  // Restoring the last item of the last page leaves the requested page out
  // of range (backend returns an empty slice, not an error); step back to
  // the last page that still has items.
  useClampPageToTotal({
    total,
    pageSize: limit,
    page,
    onPageChange: setPage,
  });

  const handleRestore = async (record: TrashItem) => {
    setRestoringIds((prev) => ({ ...prev, [record.id]: true }));
    try {
      // A fulfilled restore invalidates the "Trash" tag, so the list (and the
      // other tabs) refetches on its own — no manual refetch needed.
      const result = await restoreTrashEntity({
        entity,
        id: record.id,
      }).unwrap();
      notification.success({
        message: "Restaurado con éxito",
        description:
          result.reappears_in.length > 0
            ? `Volvió a verse en: ${result.reappears_in
                .map((item) => item.title)
                .join(", ")}`
            : "No está vinculado a ningún contenido actualmente.",
      });
      if (result.warnings.length > 0) {
        notification.warning({
          message: "Advertencias al restaurar",
          description: result.warnings.join(" "),
        });
      }
    } catch (error: any) {
      notification.error({
        message: "Error",
        description:
          error?.data?.message ?? "No se pudo restaurar el elemento.",
      });
    } finally {
      setRestoringIds((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
    }
  };

  const columns: ColumnsType<TrashItem> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Título",
      key: "title",
      render: (_: unknown, record: TrashItem) => (
        <Space size={4}>
          <Text>{record.title}</Text>
          {record.type && <Tag>{record.type}</Tag>}
        </Space>
      ),
    },
    {
      title: "Eliminado",
      dataIndex: "deleted_at",
      key: "deleted_at",
      render: (deletedAt: string) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(deletedAt).format("DD/MM/YYYY HH:mm")}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(deletedAt).locale("es").fromNow()}
          </Text>
        </Space>
      ),
    },
    {
      title: "Eliminado por",
      key: "deleted_by",
      render: (_: unknown, record: TrashItem) =>
        record.deleted_by ? (
          <Space direction="vertical" size={0}>
            <Text>{record.deleted_by.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.deleted_by.email}
            </Text>
          </Space>
        ) : (
          <Tooltip title="Eliminado antes de que existiera el registro de actividad.">
            <Tag color="default">Desconocido</Tag>
          </Tooltip>
        ),
    },
    {
      title: "Aparece en",
      key: "appears_in",
      render: (_: unknown, record: TrashItem) =>
        record.appears_in.length === 0 ? (
          "—"
        ) : (
          <Space size={4} wrap>
            {record.appears_in.map((item) => (
              <Tooltip key={`${item.type}-${item.id}`} title={formatReference(item)}>
                <Tag
                  style={{
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                >
                  {formatReference(item)}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: unknown, record: TrashItem) => (
        <Popconfirm
          title={`¿Restaurar ${label.article} ${label.singular} #${record.id} "${record.title}"?`}
          description={
            record.appears_in.length > 0
              ? `Volverá a verse en: ${record.appears_in
                  .map((item) => item.title)
                  .join(", ")}`
              : "No está vinculado a ningún contenido actualmente."
          }
          okText="Restaurar"
          cancelText="Cancelar"
          onConfirm={() => handleRestore(record)}
        >
          <Button type="primary" ghost loading={!!restoringIds[record.id]}>
            Restaurar
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los elementos eliminados."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      <Table<TrashItem>
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={isFetching}
        locale={{
          emptyText: (
            <Empty description="No hay elementos eliminados en este período." />
          ),
        }}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: false,
          onChange: (newPage) => setPage(newPage),
        }}
      />
    </Space>
  );
};

export default TrashTable;
